import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, Mail, MousePointerClick, Eye, AlertTriangle, ArrowUpRight, Info, Copy, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_LABELS = { english: 'EN', finnish: 'FI', spanish: 'ES', german: 'DE' };
const LANG_COLORS = {
  english: 'bg-[#007AFF]/10 text-[#007AFF]',
  finnish: 'bg-[#00A3FF]/10 text-[#00A3FF]',
  spanish: 'bg-[#FFB800]/10 text-[#FFB800]',
  german: 'bg-[#FF3B30]/10 text-[#FF3B30]',
};

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <span className="text-xs text-[#808080] uppercase tracking-widest">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{value}</p>
    {sub && <p className="text-xs text-[#808080] mt-1">{sub}</p>}
  </div>
);

const RateBar = ({ label, rate, color }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-[#808080] w-20 shrink-0">{label}</span>
    <div className="flex-1 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(rate, 100)}%`, backgroundColor: color }} />
    </div>
    <span className="text-xs text-white w-12 text-right font-mono">{rate}%</span>
  </div>
);

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [webhookCopied, setWebhookCopied] = useState(false);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/resend`;

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics/overview`, { withCredentials: true });
      setOverview(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCampaignDetail = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/campaigns/${id}/analytics`, { withCredentials: true });
      setCampaignDetail(res.data);
    } catch (e) { toast.error('Failed to load analytics'); }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const openDetail = (id) => {
    setSelectedCampaign(id);
    setDetailTab('overview');
    fetchCampaignDetail(id);
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setWebhookCopied(true);
    toast.success('Webhook URL copied!');
    setTimeout(() => setWebhookCopied(false), 2000);
  };

  if (!overview) {
    return (
      <div className="flex-1 p-8 flex items-center justify-center">
        <div className="text-[#808080]">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
            <TrendingUp size={28} className="inline mr-2 text-[#00FF99]" />
            Campaign Analytics
          </h1>
          <p className="text-[#B0B0B0] text-sm mt-1">Track email performance across all campaigns</p>
        </div>
      </div>

      {/* Webhook Setup Banner */}
      <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-4 mb-6 flex items-center gap-4">
        <Info size={18} className="text-[#007AFF] shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-white">
            <strong>Setup Required:</strong> Add this webhook URL in your{' '}
            <a href="https://resend.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-[#00FF99] hover:underline">
              Resend Dashboard → Webhooks
            </a>
          </p>
          <p className="text-xs text-[#808080] mt-1">Select events: <span className="text-white">email.delivered, email.opened, email.clicked, email.bounced</span></p>
        </div>
        <div className="flex items-center gap-2">
          <code className="text-xs text-[#00FF99] bg-[#00FF99]/5 px-3 py-1.5 rounded font-mono border border-[#00FF99]/20 max-w-[300px] truncate" data-testid="webhook-url">
            {webhookUrl}
          </code>
          <button onClick={copyWebhook} data-testid="copy-webhook-btn"
            className="p-2 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-white transition-colors duration-200">
            {webhookCopied ? <CheckCheck size={16} className="text-[#00FF99]" /> : <Copy size={16} />}
          </button>
        </div>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Mail} label="Total Sent" value={overview.total_emails} sub={`${overview.total_campaigns} campaigns`} color="#007AFF" />
        <StatCard icon={Eye} label="Opened" value={overview.total_opened} sub={`${overview.overall_open_rate}% open rate`} color="#00FF99" />
        <StatCard icon={MousePointerClick} label="Clicked" value={overview.total_clicked} sub={`${overview.overall_click_rate}% click rate`} color="#FFB800" />
        <StatCard icon={AlertTriangle} label="Bounced" value={overview.total_bounced} sub="Total bounces" color="#FF3B30" />
      </div>

      {/* Rates */}
      <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5 mb-8">
        <h3 className="text-sm text-[#808080] uppercase tracking-widest mb-4">Overall Performance</h3>
        <div className="space-y-3">
          <RateBar label="Open Rate" rate={overview.overall_open_rate} color="#00FF99" />
          <RateBar label="Click Rate" rate={overview.overall_click_rate} color="#FFB800" />
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg overflow-hidden">
        <div className="p-4 border-b border-[rgba(255,255,255,0.12)]">
          <h3 className="text-sm text-[#808080] uppercase tracking-widest">Campaign Performance</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[rgba(255,255,255,0.12)] hover:bg-transparent">
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Campaign</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Sent</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Opened</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Clicked</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Bounced</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Open Rate</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest w-20">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overview.campaigns.map(c => (
              <TableRow key={c.campaign_id} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-[#1A1A1A]">
                <TableCell>
                  <div>
                    <p className="text-white font-medium text-sm">{c.name}</p>
                    <p className="text-xs text-[#808080]">{c.subject}</p>
                  </div>
                </TableCell>
                <TableCell className="text-white font-mono text-sm">{c.sent}</TableCell>
                <TableCell className="text-[#00FF99] font-mono text-sm">{c.opened}</TableCell>
                <TableCell className="text-[#FFB800] font-mono text-sm">{c.clicked}</TableCell>
                <TableCell className="text-[#FF3B30] font-mono text-sm">{c.bounced}</TableCell>
                <TableCell>
                  <span className="text-sm font-mono text-white">{c.open_rate}%</span>
                </TableCell>
                <TableCell>
                  <button onClick={() => openDetail(c.campaign_id)} data-testid={`analytics-detail-${c.campaign_id}`}
                    className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-[#00FF99] transition-colors duration-200">
                    <ArrowUpRight size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {overview.campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#808080] py-8">
                  No sent campaigns yet. Analytics will appear after you send your first campaign.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Campaign Detail Dialog */}
      <Dialog open={!!selectedCampaign} onOpenChange={(v) => { if (!v) { setSelectedCampaign(null); setCampaignDetail(null); } }}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {campaignDetail?.campaign_name || 'Campaign Analytics'}
            </DialogTitle>
          </DialogHeader>

          {campaignDetail && (
            <div className="space-y-6 mt-2">
              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-white font-mono">{campaignDetail.total_sent}</p>
                  <p className="text-[10px] text-[#808080] uppercase">Sent</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#00FF99] font-mono">{campaignDetail.open_rate}%</p>
                  <p className="text-[10px] text-[#808080] uppercase">Open Rate</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#FFB800] font-mono">{campaignDetail.click_rate}%</p>
                  <p className="text-[10px] text-[#808080] uppercase">Click Rate</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#FF3B30] font-mono">{campaignDetail.bounce_rate}%</p>
                  <p className="text-[10px] text-[#808080] uppercase">Bounce Rate</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-[rgba(255,255,255,0.12)]">
                {['overview', 'openers', 'clickers', 'languages'].map(tab => (
                  <button key={tab} onClick={() => setDetailTab(tab)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-200 border-b-2 ${detailTab === tab ? 'text-[#00FF99] border-[#00FF99]' : 'text-[#808080] border-transparent hover:text-white'}`}>
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {detailTab === 'overview' && (
                <div className="space-y-3">
                  <RateBar label="Open Rate" rate={campaignDetail.open_rate} color="#00FF99" />
                  <RateBar label="Click Rate" rate={campaignDetail.click_rate} color="#FFB800" />
                  <RateBar label="Bounce Rate" rate={campaignDetail.bounce_rate} color="#FF3B30" />
                  {campaignDetail.timeline.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs text-[#808080] uppercase tracking-widest mb-3">Activity Timeline</h4>
                      <div className="space-y-2">
                        {campaignDetail.timeline.map(d => (
                          <div key={d.date} className="flex items-center gap-3 text-xs">
                            <span className="text-[#808080] w-24 font-mono">{d.date}</span>
                            <span className="text-[#00FF99]">{d.opens} opens</span>
                            <span className="text-[#FFB800]">{d.clicks} clicks</span>
                            {d.bounces > 0 && <span className="text-[#FF3B30]">{d.bounces} bounces</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'openers' && (
                <div>
                  <p className="text-xs text-[#808080] mb-3">{campaignDetail.openers.length} contacts opened this email</p>
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {campaignDetail.openers.map((o, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded hover:bg-[#0A0A0A]">
                        <div>
                          <p className="text-sm text-white">{o.name || 'Unknown'}</p>
                          <p className="text-xs text-[#808080]">{o.email}</p>
                        </div>
                        <span className="text-[10px] text-[#808080]">{o.opened_at?.slice(0, 16) || ''}</span>
                      </div>
                    ))}
                    {campaignDetail.openers.length === 0 && (
                      <p className="text-sm text-[#808080] text-center py-4">No opens tracked yet</p>
                    )}
                  </div>
                </div>
              )}

              {detailTab === 'clickers' && (
                <div>
                  <p className="text-xs text-[#808080] mb-3">{campaignDetail.clickers.length} contacts clicked a link</p>
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {campaignDetail.clickers.map((c, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded hover:bg-[#0A0A0A]">
                        <div>
                          <p className="text-sm text-white">{c.name || 'Unknown'}</p>
                          <p className="text-xs text-[#808080]">{c.email}</p>
                        </div>
                        <span className="text-[10px] text-[#808080]">{c.clicked_at?.slice(0, 16) || ''}</span>
                      </div>
                    ))}
                    {campaignDetail.clickers.length === 0 && (
                      <p className="text-sm text-[#808080] text-center py-4">No clicks tracked yet</p>
                    )}
                  </div>
                </div>
              )}

              {detailTab === 'languages' && (
                <div>
                  <p className="text-xs text-[#808080] mb-3">Performance by language</p>
                  <div className="space-y-3">
                    {Object.entries(campaignDetail.language_stats).map(([lang, stats]) => (
                      <div key={lang} className="bg-[#0A0A0A] rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${LANG_COLORS[lang] || LANG_COLORS.english}`}>
                            {LANG_LABELS[lang] || lang}
                          </span>
                          <span className="text-xs text-[#808080]">{stats.sent} emails</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-[#808080]">Opens: </span>
                            <span className="text-[#00FF99] font-mono">{stats.opened} ({stats.sent ? Math.round(stats.opened/stats.sent*100) : 0}%)</span>
                          </div>
                          <div>
                            <span className="text-[#808080]">Clicks: </span>
                            <span className="text-[#FFB800] font-mono">{stats.clicked} ({stats.sent ? Math.round(stats.clicked/stats.sent*100) : 0}%)</span>
                          </div>
                          <div>
                            <span className="text-[#808080]">Bounces: </span>
                            <span className="text-[#FF3B30] font-mono">{stats.bounced}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {Object.keys(campaignDetail.language_stats).length === 0 && (
                      <p className="text-sm text-[#808080] text-center py-4">No language data available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {!campaignDetail && (
            <div className="py-8 text-center text-[#808080]">Loading analytics...</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
